/**
 * Query param key for the settings modal overlay (`?settings=profile`).
 * Keeps pathname stable for history and tab matching.
 */
export const SETTINGS_SEARCH_PARAM = 'settings'

/**
 * Sub-tab within a settings section for memory-routed pages (`?settings_tab=domain_control`).
 * See {@link ./settings_modal_memory_tabs.js} for slug → path mapping per section.
 */
export const SETTINGS_TAB_SEARCH_PARAM = 'settings_tab'

/** Feature-check variant key for “join an org” access nav (User access first vs domain control). */
export const OBI_HOME_NEW_JOIN_AN_ORG_VARIANT = 'obi_home_new_join_an_org'

/**
 * Legacy path prefix for bookmarks; {@link parseSectionFromSettingsPathname} and the modal
 * migration replace with `?settings=` on the host path.
 */
const SETTINGS_PATH_PREFIX = '/app/settings'

/**
 * Merge `settings=<section>` into existing search (preserves other query params).
 * Profile opens with sub-routes (General / Login); when `settingsTab` is omitted, `settings_tab=general`
 * is applied so the browser URL matches the memory router before first paint — avoiding a second
 * `history.replace` from tab sync (host route re-render / “reload” under the modal).
 * @param {string} section — {@link SETTINGS_SECTION} string value
 * @param {string} [existingSearch] — `location.search` including `?` or empty
 * @param {string} [settingsTab] — optional {@link SETTINGS_TAB_SEARCH_PARAM} slug (memory-route tabs)
 * @returns {string} — search string including leading `?` when non-empty
 */
export const mergeSettingsSearch = (section, existingSearch = '', settingsTab = undefined) => {
  const params = new URLSearchParams(existingSearch)
  params.set(SETTINGS_SEARCH_PARAM, section)
  let resolvedTab
  if (settingsTab != null && settingsTab !== '') {
    resolvedTab = settingsTab
  } else if (section === 'profile') {
    resolvedTab = 'general'
  } else {
    resolvedTab = undefined
  }
  if (resolvedTab != null && resolvedTab !== '') {
    params.set(SETTINGS_TAB_SEARCH_PARAM, resolvedTab)
  } else {
    params.delete(SETTINGS_TAB_SEARCH_PARAM)
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** @param {string} [existingSearch] */
export const parseSettingsSectionFromSearch = (existingSearch = '') => {
  const raw = new URLSearchParams(existingSearch).get(SETTINGS_SEARCH_PARAM)
  if (raw == null || raw === '') {
    return null
  }
  return raw
}

/**
 * True when settings IA rebrand is on and the location has a non-empty `settings` query param
 * (same signal as {@link ./settings_modal.js} for “modal flow” URL state).
 * @param {{ isRebrandSettingsIaEnabled: boolean, search: string }} args
 */
export function isSettingsModalNavigationActive({ isRebrandSettingsIaEnabled, search }) {
  if (!isRebrandSettingsIaEnabled) {
    return false
  }
  return parseSettingsSectionFromSearch(search) != null
}

/** @param {string} [existingSearch] */
export const parseSettingsTabFromSearch = (existingSearch = '') => {
  const raw = new URLSearchParams(existingSearch).get(SETTINGS_TAB_SEARCH_PARAM)
  if (raw == null || raw === '') {
    return null
  }
  return raw
}

/** Remove `settings` and `settings_tab` from search (e.g. when closing the modal). */
export const stripSettingsSearch = (existingSearch = '') => {
  const params = new URLSearchParams(existingSearch)
  params.delete(SETTINGS_SEARCH_PARAM)
  params.delete(SETTINGS_TAB_SEARCH_PARAM)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const parseSectionFromSettingsPathname = (pathname) => {
  const prefix = `${SETTINGS_PATH_PREFIX}/`
  if (!pathname.startsWith(prefix)) {
    return null
  }
  const encoded = pathname.slice(prefix.length)
  if (!encoded) {
    return null
  }
  return decodeURIComponent(encoded)
}

/** Mobile-only: show group list before picking a section. */
export const SETTINGS_MOBILE_LIST_SENTINEL = '_list'

/**
 * Main-pane render strategy for {@link ./section_content_registry.js} and {@link ./settingsConfig.ts}.
 * @readonly
 * @enum {string}
 */
export const SETTINGS_MODAL_PANE_KIND = Object.freeze({
  COLUMN: 'column',
  MEMORY_ROUTE: 'memory_route',
  USERS_PAGE: 'users_page',
  GROUPS_SHELL: 'groups_shell',
})

/**
 * Values for `?settings=` when the settings modal is open.
 * Keep in sync with tests and {@link ./legacy_admin_routes.js} / admin redirects.
 */
export const SETTINGS_SECTION = {
  profile: 'profile',
  branding: 'branding',
  link: 'link',
  login_preferences: 'login_preferences',
  workflows: 'workflows',
  security: 'security',
  ai_notetaker: 'ai_notetaker',
  assistant: 'assistant',
  contacts_settings: 'contacts_settings',
  adminUsers: 'admin/users',
  adminInvitations: 'admin/invitations',
  adminMigrations: 'admin/migrations',
  adminGroups: 'admin/groups',
  adminDashboard: 'admin/dashboard',
  adminAccess: 'admin/access',
  adminBilling: 'admin/billing',
  /** @deprecated Use {@link SETTINGS_SECTION.adminOrganization}. Kept for legacy `?settings=` URLs. */
  adminBranding: 'admin/branding',
  adminOrganization: 'admin/organization',
  adminPermissions: 'admin/permissions',
  adminSecurity: 'admin/security',
  adminNotetaker: 'admin/notetaker',
  adminManagedEventTypes: 'admin/managed_event_types',
  adminManagedWorkflows: 'admin/managed_workflows',
}

export const SETTINGS_ADMIN_PEOPLE_SECTIONS = [
  SETTINGS_SECTION.adminInvitations,
  SETTINGS_SECTION.adminMigrations,
  SETTINGS_SECTION.adminUsers,
]

/** Org admin center sections (excluding Users / Groups shells). */
export const SETTINGS_ADMIN_ORG_SECTIONS = [
  SETTINGS_SECTION.adminBilling,
  SETTINGS_SECTION.adminDashboard,
  SETTINGS_SECTION.adminAccess,
  SETTINGS_SECTION.adminManagedEventTypes,
  SETTINGS_SECTION.adminManagedWorkflows,
  SETTINGS_SECTION.adminNotetaker,
  SETTINGS_SECTION.adminOrganization,
  SETTINGS_SECTION.adminPermissions,
  SETTINGS_SECTION.adminSecurity,
]

export const SETTINGS_PERSONAL_ACCOUNT_SECTIONS = [
  SETTINGS_SECTION.ai_notetaker,
  SETTINGS_SECTION.assistant,
  SETTINGS_SECTION.branding,
  SETTINGS_SECTION.contacts_settings,
  SETTINGS_SECTION.link,
  SETTINGS_SECTION.login_preferences,
  SETTINGS_SECTION.profile,
  SETTINGS_SECTION.security,
  SETTINGS_SECTION.workflows,
]

/** Personal sections valid in the modal (e.g. `?settings=` deep links) but omitted from the left nav. */
export const SETTINGS_PERSONAL_ACCOUNT_SECTIONS_HIDDEN_FROM_NAV = [
  SETTINGS_SECTION.login_preferences,
]

export const isSettingsAdminPeopleSection = (section) => (
  section != null && SETTINGS_ADMIN_PEOPLE_SECTIONS.includes(section)
)

const isSettingsAdminGroupsSection = (section) => (
  section === SETTINGS_SECTION.adminGroups
)

export const isSettingsAdminSection = (section) => (
  section != null && (
    isSettingsAdminPeopleSection(section)
    || isSettingsAdminGroupsSection(section)
    || SETTINGS_ADMIN_ORG_SECTIONS.includes(section)
  )
)
